import { useMemo, useState } from "react";
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronRight,
  ListPlus,
  Plus,
  Send,
  X,
} from "lucide-react";
import SuppressNotificationsToggle from "components/SuppressNotificationsToggle";
import { parsePendText } from "utilities/pendParser";
import {
  blankPendRow,
  normalizePendRow,
  filledPendRows,
  hasPartialPendRow,
  pendRowsToPayload,
} from "utilities/pendComposer";
import { usePendCategories } from "hooks/crm/usePends";

/**
 * SOUND-619 — the Pends panel for a MID application.
 *
 * Redesigned rather than extended (CPO call): the previous UI was a flat list
 * that mixed open and resolved items, plus two cramped inline inputs that could
 * only ever add ONE pend — which is what produced one Telegram message per item.
 *
 * What the design is doing, and why:
 *  - OPEN pends lead and carry a warning tone; resolved ones collapse behind a
 *    disclosure. Outstanding work is the thing an operator is scanning for.
 *  - The composer is repeatable rows submitted as ONE request, with the
 *    consequence stated inline ("one message listing all of them"). Naming the
 *    notification consequence at the point of action is the same principle as
 *    the SOUND-618 confirmation dialog.
 *  - Selection drives a bulk resolve, so closing five items is one action and
 *    one merchant message rather than five of each.
 *  - Both the add and the resolve paths expose the SOUND-618 suppression
 *    toggle, since both dispatch to the merchant.
 *
 * Presentational: the page owns the API calls and passes handlers.
 */
export default function PendsPanel({
  pends = [],
  isAdmin = false,
  submitting = false,
  maxPerRequest = 20,
  onAddPends,
  onResolvePends,
}) {
  // SOUND-621: a row is { category, detail, description } — controlled + free
  // text — not the free-text `type` that produced 36 spellings of eight
  // categories. The vocabulary is FETCHED, never hardcoded here: a frontend copy
  // is exactly how that drift happened.
  const { categories } = usePendCategories();
  const [rows, setRows] = useState([blankPendRow()]);
  const [pasteText, setPasteText] = useState("");
  const [selected, setSelected] = useState([]);
  const [showResolved, setShowResolved] = useState(false);
  const [suppressAdd, setSuppressAdd] = useState(false);
  const [suppressResolve, setSuppressResolve] = useState(false);

  // Live preview of what a paste would produce. Parsing is deliberately NOT
  // applied on every keystroke — it appends editable rows only when the user
  // asks, so it can never clobber an edit they already made.
  const detected = useMemo(() => parsePendText(pasteText), [pasteText]);

  /**
   * How a STORED pend reads. Mirrors pendDisplayType() in sound-api's
   * config/pendCategories.mjs — label, plus the detail when there is one.
   *
   * Falls back to the raw `type` for a pend written before the vocabulary: 161
   * rows were normalised, but the field is only retired once nothing writes it,
   * and an operator's real words beat a blank.
   */
  const labelOf = useMemo(() => {
    const byKey = Object.fromEntries(categories.map((c) => [c.key, c.label]));
    return (p) => {
      const label = byKey[p?.category];
      if (!label) return p?.type || "—";
      return p.detail ? `${label} — ${p.detail}` : label;
    };
  }, [categories]);

  const { open, resolved } = useMemo(
    () => ({
      open: pends.filter((p) => !p.resolvedAt),
      resolved: pends.filter((p) => p.resolvedAt),
    }),
    [pends]
  );

  // Both TOTAL — see utilities/pendComposer. `partial` runs on every render, so
  // a throw here is not a bad value, it is a blank panel.
  const filled = filledPendRows(rows);
  const partial = hasPartialPendRow(rows);
  const atCap = rows.length >= maxPerRequest;

  const patchRow = (i, patch) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRow = () => setRows((rs) => [...rs, blankPendRow()]);

  /**
   * Append the parsed pends as editable rows and clear the paste box.
   * Drops a single leading blank row so pasting into a fresh panel does not
   * leave an empty first row behind.
   */
  const applyPaste = () => {
    if (!detected.length) return;
    setRows((rs) => {
      const base =
        rs.length === 1 && !rs[0].category && !rs[0].detail && !rs[0].description ? [] : rs;
      // Parsed rows are normalised, so a parser that omits a field cannot
      // desynchronise the composer the way removeRow did.
      return [...base, ...detected.map(normalizePendRow)].slice(0, maxPerRequest);
    });
    setPasteText("");
  };
  // Removing the last row leaves a blank one rather than an empty composer.
  // This built the row inline and was MISSED when `detail` was added, so it
  // emitted the old shape and `partial` threw on undefined.trim() — which
  // blanked the whole panel. One definition now, in pendComposer.
  const removeRow = (i) =>
    setRows((rs) => (rs.length === 1 ? [blankPendRow()] : rs.filter((_, idx) => idx !== i)));

  const submitAdd = async () => {
    if (!filled.length || partial || submitting) return;
    // SOUND-621: sends `category` + `detail`, the shape the API's vocabulary
    // expects. It still accepts the legacy `type`, but a composer that keeps
    // sending free text would just restart the drift the normalisation cleared.
    await onAddPends(pendRowsToPayload(rows), { suppressNotifications: suppressAdd });
    setRows([blankPendRow()]);
    setSuppressAdd(false);
  };

  const submitResolve = async () => {
    if (!selected.length || submitting) return;
    await onResolvePends(selected, { suppressNotifications: suppressResolve });
    setSelected([]);
    setSuppressResolve(false);
  };

  const allOpenSelected = open.length > 0 && selected.length === open.length;

  return (
    <section className="surface rounded-xl ring-1 ring-border overflow-hidden">
      {/* ── Header ─────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-border">
        <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-warning" />
          Pends
        </h2>
        <span
          className={`badge ${
            open.length ? "bg-warning/15 text-warning" : "bg-muted text-muted-foreground"
          }`}
        >
          {open.length} open
        </span>
      </div>

      <div className="p-6 space-y-5">
        {/* ── Open pends ───────────────────────────────────── */}
        {open.length > 0 ? (
          <div className="space-y-2">
            {isAdmin && open.length > 1 && (
              <button
                type="button"
                className="text-xs font-medium text-muted-foreground hover:text-foreground"
                onClick={() => setSelected(allOpenSelected ? [] : open.map((p) => p.pendId))}
                disabled={submitting}
              >
                {allOpenSelected ? "Clear selection" : `Select all ${open.length}`}
              </button>
            )}
            <ul className="space-y-2">
              {open.map((p) => {
                const checked = selected.includes(p.pendId);
                return (
                  <li
                    key={p.pendId}
                    className={`rounded-lg border p-3 transition-colors ${
                      checked ? "border-brand/40 bg-brand/[0.06]" : "border-border bg-warning/[0.04]"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {isAdmin && (
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) =>
                            setSelected((ids) =>
                              e.target.checked
                                ? [...ids, p.pendId]
                                : ids.filter((x) => x !== p.pendId)
                            )
                          }
                          disabled={submitting}
                          className="mt-1 h-4 w-4 shrink-0 rounded border-border text-brand focus:ring-brand/40"
                          aria-label={`Select pend: ${labelOf(p)}`}
                        />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-foreground">{labelOf(p)}</p>
                        <p className="text-sm text-muted-foreground mt-0.5">{p.description}</p>
                        <p className="text-xs text-muted-foreground/70 mt-1.5">
                          Added by {p.createdBy?.name || p.createdBy?.email || "Unknown"}
                          {p.createdAt ? ` · ${new Date(p.createdAt).toLocaleDateString()}` : ""}
                        </p>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            No open pends. {resolved.length > 0 && "Everything raised has been resolved."}
          </p>
        )}

        {/* ── Bulk resolve bar (only once something is selected) ── */}
        {isAdmin && selected.length > 0 && (
          <div className="rounded-lg border border-brand/40 bg-brand/[0.06] p-3 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm font-medium text-foreground">
                {selected.length} selected
              </span>
              <div className="flex items-center gap-2">
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => setSelected([])}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button className="btn btn-primary btn-sm" onClick={submitResolve} disabled={submitting}>
                  <Check className="h-4 w-4" />
                  {submitting ? "Resolving..." : `Resolve ${selected.length}`}
                </button>
              </div>
            </div>
            <SuppressNotificationsToggle
              checked={suppressResolve}
              onChange={setSuppressResolve}
              disabled={submitting}
              visible={isAdmin}
              hint="Resolving normally tells the merchant. Tick to record it silently."
            />
          </div>
        )}

        {/* ── Resolved, collapsed ──────────────────────────── */}
        {resolved.length > 0 && (
          <div className="border-t border-border pt-4">
            <button
              type="button"
              className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground"
              onClick={() => setShowResolved((s) => !s)}
            >
              {showResolved ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
              {resolved.length} resolved
            </button>
            {showResolved && (
              <ul className="mt-3 space-y-2">
                {resolved.map((p) => (
                  <li key={p.pendId} className="rounded-lg border border-border p-3 opacity-70">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground line-through decoration-muted-foreground/50">
                          {labelOf(p)}
                        </p>
                        <p className="text-sm text-muted-foreground mt-0.5">{p.description}</p>
                        <p className="text-xs text-muted-foreground/70 mt-1.5">
                          Resolved by {p.resolvedBy?.name || p.resolvedBy?.email || "Unknown"}
                          {p.resolvedAt ? ` · ${new Date(p.resolvedAt).toLocaleDateString()}` : ""}
                        </p>
                      </div>
                      <span className="badge bg-success/15 text-success shrink-0">
                        <Check className="h-3 w-3" />
                        Resolved
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* ── Composer ─────────────────────────────────────── */}
        {isAdmin && (
          <div className="border-t border-border pt-5 space-y-4">
            <div className="flex items-baseline justify-between gap-3">
              <h3 className="text-sm font-semibold text-foreground">Add pends</h3>
              <span className="text-xs text-muted-foreground">
                {rows.length} of {maxPerRequest} max
              </span>
            </div>

            {/*
              PASTE FIRST. The real workflow is a VA receiving an underwriter's
              pend list and pasting it whole; typing rows one at a time is the
              slow path, not the default. Parsing appends EDITABLE rows rather
              than submitting, because real pend text defeats any tidy rule —
              see utilities/pendParser.js.
            */}
            <div className="rounded-lg border border-dashed border-border bg-muted/20 p-3 space-y-2">
              <label className="block text-xs font-medium text-foreground">
                Paste the pend list
                <span className="font-normal text-muted-foreground">
                  {" "}— numbered, bulleted, or one per line. Paragraphs separated by a
                  blank line stay together.
                </span>
              </label>
              <textarea
                className="input w-full font-mono text-xs"
                rows={4}
                placeholder={"1. Please provide a pre-printed voided check\n2. Remove the auto-enrollment text from the website\n3. Business Bank Statements - 3 Most Recent Months"}
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
                disabled={submitting}
              />
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs text-muted-foreground">
                  {pasteText.trim()
                    ? `${detected.length} pend${detected.length === 1 ? "" : "s"} detected`
                    : "Nothing pasted yet"}
                </span>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={applyPaste}
                  disabled={submitting || detected.length === 0}
                >
                  <ListPlus className="h-4 w-4" />
                  Add {detected.length || ""} to the list
                </button>
              </div>
              {detected.length > 0 && rows.length - 1 + detected.length > maxPerRequest && (
                <p className="text-xs text-warning">
                  That would exceed {maxPerRequest} pends. Only the first{" "}
                  {maxPerRequest} will be added — send the rest as a second batch.
                </p>
              )}
            </div>

            {/* Editable rows — the reviewable result of parsing, or manual entry. */}
            <div className="space-y-2">
              {rows.map((row, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2 rounded-lg border border-border p-2"
                >
                  <span className="mt-2 w-5 shrink-0 text-center text-xs text-muted-foreground">
                    {i + 1}
                  </span>
                  <div className="min-w-0 flex-1 space-y-2">
                    <div className="flex gap-2">
                      {/* CONTROLLED. Options come from GET /pends/categories, so
                          the list here can never drift from the API's. */}
                      <select
                        className="select w-44 shrink-0"
                        value={row.category}
                        onChange={(e) => patchRow(i, { category: e.target.value })}
                        disabled={submitting}
                        aria-label={`Pend ${i + 1} category`}
                      >
                        {/* A real unselected state. Defaulting to the first
                            category would file every unreviewed row under it. */}
                        <option value="">Category…</option>
                        {categories.map((c) => (
                          <option key={c.key} value={c.key}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                      {/* FREE TEXT. This is where "Voided Check" goes — the
                          subcategory operators were already typing by hand. */}
                      <input
                        type="text"
                        className="input w-full"
                        placeholder="Detail (optional) — e.g. Voided Check"
                        value={row.detail}
                        onChange={(e) => patchRow(i, { detail: e.target.value })}
                        disabled={submitting}
                        aria-label={`Pend ${i + 1} detail`}
                      />
                    </div>
                    {/* A textarea, not an input: real descriptions are paragraphs. */}
                    <textarea
                      className="input w-full"
                      rows={2}
                      placeholder="What the merchant needs to provide or fix"
                      value={row.description}
                      onChange={(e) => patchRow(i, { description: e.target.value })}
                      disabled={submitting}
                      aria-label={`Pend ${i + 1} description`}
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn-ghost px-2 shrink-0"
                    onClick={() => removeRow(i)}
                    disabled={
                      submitting ||
                      (rows.length === 1 && !row.category && !row.detail && !row.description)
                    }
                    aria-label={`Remove pend ${i + 1}`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>

            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={addRow}
              disabled={submitting || atCap}
              title={atCap ? `Maximum ${maxPerRequest} pends per submission` : undefined}
            >
              <Plus className="h-4 w-4" />
              Add a row manually
            </button>

            {partial && (
              <p className="text-xs text-destructive">
                Every row needs both a type and a description. Complete or remove the
                incomplete row — a partial row rejects the whole batch.
              </p>
            )}

            <SuppressNotificationsToggle
              checked={suppressAdd}
              onChange={setSuppressAdd}
              disabled={submitting}
              visible={isAdmin}
            />

            <div className="flex items-center justify-between gap-3 pt-1">
              {/* States the consequence at the point of action — the whole
                  reason this panel batches. */}
              <p className="text-xs text-muted-foreground">
                {filled.length === 0
                  ? "Nothing to add yet."
                  : suppressAdd
                  ? `${filled.length} pend${filled.length > 1 ? "s" : ""} — the merchant will not be notified.`
                  : filled.length === 1
                  ? "The merchant gets one message."
                  : `${filled.length} pends — the merchant gets ONE message listing all of them.`}
              </p>
              <button
                className="btn btn-primary shrink-0"
                onClick={submitAdd}
                disabled={submitting || !filled.length || partial}
              >
                <Send className="h-4 w-4" />
                {submitting
                  ? "Adding..."
                  : filled.length > 1
                  ? `Add ${filled.length} pends`
                  : "Add pend"}
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
